// @path: /src/pages/shelbula/workspaces/WorkspaceDetailPanel.jsx
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { LayoutDashboard, History, UploadCloud, FolderTree, ChevronDown, ChevronUp, RefreshCw, Loader, MessageSquareText, HardDrive, MessagesSquare, GitBranch, Search, MoreVertical, Trash2, CheckSquare2, Square, Copy, Check, Loader2, Clock, CheckCircle2, XCircle, Play, Bot } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { apiManager } from './apiManager';
import FileExplorer from './FileExplorer';
import WorkspaceHistoryTab from './WorkspaceHistoryTab'; 
import ConfirmConversationDeleteModal from './ConfirmationModals/ConfirmConversationDeleteModal';
import ConfirmRunAgentsModal from './ConfirmationModals/ConfirmRunAgentsModal';
import { areWorkspaceContextIdsEqual } from '@/utils/workspaceContext';
import { useDragDrop } from '@/managers/DragDropManager';
import { useConversation } from '@/managers/ConvoManager/ConvoManager';
import { authManager } from '@/managers/AuthManager';
import { botManager } from '@/managers/BotManager';
import { db } from '@/firebase/config';
import { addDoc, collection, deleteDoc, doc, onSnapshot, serverTimestamp, updateDoc } from 'firebase/firestore';
import { getWorkspaceSubcollectionRef, getWorkspaceSubdocRef } from '@/utils/workspaceFirestore';

const normalizeDate = (dateInput) => {
    if (!dateInput) return null;
    if (dateInput?.toDate && typeof dateInput.toDate === 'function') return dateInput.toDate();
    if (dateInput?._seconds) return new Date(dateInput._seconds * 1000 + (dateInput._nanoseconds || 0) / 1000000);
    if (dateInput instanceof Date) return dateInput;

    const parsed = new Date(dateInput);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatRelativeDate = (dateInput) => {
    const date = normalizeDate(dateInput);
    if (!date) return 'Just now';

    const diffInSeconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
    if (diffInSeconds < 60) return `${diffInSeconds}s ago`;

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 30) return `${diffInDays} days ago`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const getConversationTimestamp = (conversation) => {
    return normalizeDate(
        conversation?.lastUpdated ||
        conversation?.lastMessagePreview?.timestamp ||
        conversation?.created
    );
};

const hasVisibleWorkspaceMessages = (conversation) => {
    return Number(conversation?.messageCount || 0) > 0;
};

const hasFilePayload = (event) => Array.from(event?.dataTransfer?.types || []).includes('Files');

const normalizeSearchText = (value) => String(value || '').toLowerCase().trim();

const OverviewCardTitle = ({ icon: Icon, label }) => (
    <div className="flex items-center gap-2">
        <Icon size={14} className="text-primary" />
        <h3 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-on-surface-dim">{label}</h3>
    </div>
);

const WorkspaceContextCard = ({ rootItemCount, recentConversationCount, hasRepo }) => (
    <div className="bg-surface rounded-lg border p-3" style={{ borderColor: 'rgba(var(--border-color-rgb), 0.5)' }}>
        <div className="mb-2">
            <OverviewCardTitle icon={LayoutDashboard} label="Workspace Context" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="rounded-md border px-3 py-2.5 bg-background-dark/60" style={{ borderColor: 'rgba(var(--border-color-rgb), 0.45)' }}>
                <div className="flex items-center gap-2 mb-1 text-on-surface"><HardDrive size={13} className="text-primary" /><span className="text-[10px] font-semibold uppercase tracking-[0.12em]">Files</span></div>
                <p className="text-[12px] text-on-surface">{rootItemCount} root item{rootItemCount === 1 ? '' : 's'}</p>
            </div>
            <div className="rounded-md border px-3 py-2.5 bg-background-dark/60" style={{ borderColor: 'rgba(var(--border-color-rgb), 0.45)' }}>
                <div className="flex items-center gap-2 mb-1 text-on-surface"><MessagesSquare size={13} className="text-primary" /><span className="text-[10px] font-semibold uppercase tracking-[0.12em]">Chats</span></div>
                <p className="text-[12px] text-on-surface">{recentConversationCount} convo{recentConversationCount === 1 ? '' : 's'}</p>
            </div>
            <div className="rounded-md border px-3 py-2.5 bg-background-dark/60" style={{ borderColor: 'rgba(var(--border-color-rgb), 0.45)' }}>
                <div className="flex items-center gap-2 mb-1 text-on-surface"><GitBranch size={13} className="text-primary" /><span className="text-[10px] font-semibold uppercase tracking-[0.12em]">Repo</span></div>
                <p className="text-[12px] text-on-surface">{hasRepo ? 'Connected' : 'None'}</p>
            </div>
        </div>
    </div>
);

const updateNodeInTree = (nodes, path, updates) => {
    const newNodes = { ...nodes };
    const pathParts = path.split('/');
    let currentLevel = newNodes;

    for (let i = 0; i < pathParts.length - 1; i++) {
        const part = pathParts[i];
        currentLevel[part] = { ...currentLevel[part], children: { ...currentLevel[part].children } };
        currentLevel = currentLevel[part].children;
    }

    const finalPart = pathParts[pathParts.length - 1];
    if (currentLevel[finalPart]) {
        currentLevel[finalPart] = { ...currentLevel[finalPart], ...updates };
    }
    return newNodes;
};

const buildFileTreeFromFlatFiles = (files = []) => {
    const root = {};

    files.forEach((entry) => {
        const entryPath = String(entry?.path || '').trim();
        if (!entryPath) return;

        const parts = entryPath.split('/').filter(Boolean);
        if (parts.length === 0) return;

        let currentLevel = root;

        parts.forEach((part, index) => {
            const isLeaf = index === parts.length - 1;
            const currentPath = parts.slice(0, index + 1).join('/');
            const existingNode = currentLevel[part];
            const isDirectory = isLeaf ? entry.type === 'directory' : true;

            currentLevel[part] = {
                ...(existingNode || {}),
                name: part,
                path: currentPath,
                type: isDirectory ? 'directory' : 'file',
                children: isDirectory ? { ...(existingNode?.children || {}) } : null,
                size: isLeaf ? (entry.size ?? existingNode?.size ?? null) : (existingNode?.size ?? null),
                lastModified: isLeaf ? (entry.lastModified ?? existingNode?.lastModified ?? null) : (existingNode?.lastModified ?? null),
            };

            if (currentLevel[part].type === 'directory') {
                currentLevel = currentLevel[part].children;
            }
        });
    });

    return root;
};

const WorkspaceConversationList = ({ workspaceId }) => {
    const { conversations, currentConversationId, messages, switchToConversation, deleteConversation } = useConversation();
    const [searchQuery, setSearchQuery] = useState('');
    const [menuOpenFor, setMenuOpenFor] = useState(null);
    const [deletingConversationId, setDeletingConversationId] = useState(null);
    const [conversationPendingDelete, setConversationPendingDelete] = useState(null);
    const [showAll, setShowAll] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => { setShowAll(false); }, [workspaceId]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setMenuOpenFor(null);
            }
        };

        window.addEventListener('pointerdown', handleClickOutside);
        return () => window.removeEventListener('pointerdown', handleClickOutside);
    }, []);

    const normalizedQuery = normalizeSearchText(searchQuery);

    const items = useMemo(() => {
        return Object.values(conversations || {})
            .filter((conversation) => areWorkspaceContextIdsEqual(conversation?.workspaceContextId, workspaceId))
            .filter((conversation) => hasVisibleWorkspaceMessages(conversation))
            .filter((conversation) => {
                if (!normalizedQuery) return true;

                const loadedMessages = messages?.[conversation.id] || [];
                const messageSearchText = loadedMessages
                    .map((message) => message?.rawContent || '')
                    .join(' ');
                const searchableText = normalizeSearchText(`${conversation?.name || ''} ${messageSearchText}`);

                return searchableText.includes(normalizedQuery);
            })
            .sort((left, right) => {
                const leftTime = getConversationTimestamp(left)?.getTime() || 0;
                const rightTime = getConversationTimestamp(right)?.getTime() || 0;
                return rightTime - leftTime;
            })
            .slice(0, normalizedQuery ? 24 : undefined);
    }, [conversations, messages, normalizedQuery, workspaceId]);

    const handleDeleteConversation = useCallback(async (conversationId) => {
        const conversation = conversations?.[conversationId];
        if (!conversationId || deletingConversationId) return;
        setMenuOpenFor(null);
        setConversationPendingDelete(conversation || null);
    }, [conversations, deleteConversation, deletingConversationId]);

    const handleConfirmDeleteConversation = useCallback(async () => {
        const conversationId = conversationPendingDelete?.id;
        if (!conversationId || deletingConversationId) return;

        setDeletingConversationId(conversationId);
        try {
            await deleteConversation(conversationId);
            setConversationPendingDelete(null);
        } finally {
            setDeletingConversationId(null);
        }
    }, [conversationPendingDelete, deleteConversation, deletingConversationId]);

    if (items.length === 0 && !normalizedQuery) {
        return (
            <>
            <div className="bg-surface rounded-lg border p-3" style={{ borderColor: 'rgba(var(--border-color-rgb), 0.5)' }}>
                <div className="flex items-center justify-between gap-3 mb-2">
                    <OverviewCardTitle icon={MessageSquareText} label="Recent Conversations" />
                    <label className="relative flex items-center">
                        <Search size={12} className="absolute left-2 text-on-surface-dim pointer-events-none" />
                        <input
                            value={searchQuery}
                            onChange={(event) => setSearchQuery(event.target.value)}
                            placeholder="Search"
                            className="h-7 w-36 rounded-md border border-border-color bg-background-dark pl-7 pr-2 text-[11px] text-on-surface placeholder:text-on-surface-dim focus:outline-none focus:border-primary/40"
                        />
                    </label>
                </div>
                <p className="text-[12px] text-on-surface-dim">No saved conversations yet.</p>
            </div>
            <ConfirmConversationDeleteModal
                isOpen={Boolean(conversationPendingDelete)}
                onClose={() => setConversationPendingDelete(null)}
                onConfirm={handleConfirmDeleteConversation}
                conversationName={conversationPendingDelete?.name}
                isDeleting={Boolean(deletingConversationId)}
            />
            </>
        );
    }

    return (
        <>
        <div className="bg-surface rounded-lg border p-3" style={{ borderColor: 'rgba(var(--border-color-rgb), 0.5)' }}>
            <div className="flex items-center justify-between gap-3 mb-2">
                <OverviewCardTitle icon={MessageSquareText} label="Recent Conversations" />
                <label className="relative flex items-center">
                    <Search size={12} className="absolute left-2 text-on-surface-dim pointer-events-none" />
                    <input
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                        placeholder="Search"
                        className="h-7 w-36 rounded-md border border-border-color bg-background-dark pl-7 pr-2 text-[11px] text-on-surface placeholder:text-on-surface-dim focus:outline-none focus:border-primary/40"
                    />
                </label>
            </div>
            <div className="space-y-1.5" ref={menuRef}>
                {items.length === 0 && normalizedQuery ? (
                    <div className="rounded-md border px-3 py-2 bg-background-dark/40 text-[11px] text-on-surface-dim" style={{ borderColor: 'rgba(var(--border-color-rgb), 0.45)' }}>
                        No conversations match that search.
                    </div>
                ) : null}
                {(normalizedQuery ? items : showAll ? items : items.slice(0, 5)).map((conversation) => {
                    const isCurrent = conversation.id === currentConversationId;
                    const updatedAt = getConversationTimestamp(conversation);
                    const isDeleting = deletingConversationId === conversation.id;

                    return (
                        <div
                            key={conversation.id}
                            className="rounded-md border px-3 py-2 bg-background-dark/60"
                            style={{ borderColor: isCurrent ? 'rgba(var(--primary-rgb), 0.35)' : 'rgba(var(--border-color-rgb), 0.45)' }}
                        >
                            <div className="flex items-center justify-between gap-3">
                                <button
                                    type="button"
                                    onClick={() => switchToConversation(conversation.id)}
                                    className="min-w-0 flex-1 text-left"
                                >
                                    <p className="text-[12px] font-medium text-on-surface truncate">
                                        {conversation.name || 'New Conversation'}
                                    </p>
                                    <div className="flex items-center gap-2 text-[10px] text-on-surface-dim mt-1">
                                        <span>{conversation.messageCount || 0} message{conversation.messageCount === 1 ? '' : 's'}</span>
                                        <span className="w-1 h-1 rounded-full bg-on-surface-dim/40" />
                                        <span>{formatRelativeDate(updatedAt)}</span>
                                    </div>
                                </button>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    {isCurrent && (
                                        <span className="text-[9px] uppercase tracking-[0.12em] text-primary font-semibold">Open</span>
                                    )}
                                    {conversation.botName && (
                                        <span className="text-[9px] px-2 py-1 rounded-full bg-surface text-on-surface-dim border border-border-color">
                                            {conversation.botName}
                                        </span>
                                    )}
                                    <div className="relative">
                                        <button
                                            type="button"
                                            onClick={() => setMenuOpenFor((current) => current === conversation.id ? null : conversation.id)}
                                            className="p-1 rounded-md text-on-surface-dim hover:text-on-surface hover:bg-surface transition-colors"
                                            aria-label={`Conversation options for ${conversation.name || 'New Conversation'}`}
                                        >
                                            <MoreVertical size={14} />
                                        </button>
                                        {menuOpenFor === conversation.id && (
                                            <div className="absolute right-0 top-7 z-20 min-w-[128px] rounded-md border border-border-color bg-surface shadow-lg overflow-hidden">
                                                <button
                                                    type="button"
                                                    onClick={() => handleDeleteConversation(conversation.id)}
                                                    disabled={isDeleting}
                                                    className="w-full flex items-center gap-2 px-3 py-2 text-[11px] text-left text-red-300 hover:bg-red-500/10 disabled:opacity-50"
                                                >
                                                    <Trash2 size={12} />
                                                    {isDeleting ? 'Deleting...' : 'Delete Chat'}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
                {!normalizedQuery && !showAll && items.length > 5 && (
                    <button
                        type="button"
                        onClick={() => setShowAll(true)}
                        className="w-full pt-1 text-[10px] text-on-surface-dim hover:text-primary transition-colors"
                    >
                        Show {items.length - 5} more
                    </button>
                )}
            </div>
        </div>
        <ConfirmConversationDeleteModal
            isOpen={Boolean(conversationPendingDelete)}
            onClose={() => setConversationPendingDelete(null)}
            onConfirm={handleConfirmDeleteConversation}
            conversationName={conversationPendingDelete?.name}
            isDeleting={Boolean(deletingConversationId)}
        />
        </>
    );
};

const WorkspaceDropTarget = ({ isDragging, children, ...dragHandlers }) => {
    return (
        <div
            className="h-full relative"
            onDragEnter={dragHandlers.onDragEnter}
            onDragLeave={dragHandlers.onDragLeave}
            onDragOver={dragHandlers.onDragOver}
            onDrop={dragHandlers.onDrop}
        >
            {children}
            <AnimatePresence>
                {isDragging && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-primary/20 backdrop-blur-sm border-2 border-dashed border-primary flex flex-col items-center justify-center pointer-events-none z-20">
                        <UploadCloud size={48} className="text-primary mb-4" />
                        <p className="text-primary text-base font-semibold">Drop files into this workspace</p>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const AgentJobStatusIcon = ({ status }) => {
    if (status === 'running') return <Loader2 size={13} className="animate-spin text-primary" />;
    if (status === 'done') return <CheckCircle2 size={13} className="text-green-400" />;
    if (status === 'failed') return <XCircle size={13} className="text-red-400" />;
    return <Clock size={13} className="text-on-surface-dim" />;
};

const AgentConflictCards = ({ workspaceId, activeJobs }) => {
    const [conflictsByJob, setConflictsByJob] = useState({});
    const [resolving, setResolving] = useState({});

    // Only watch parallel jobs that are done (finished merging, possibly with conflicts)
    const parallelDoneJobs = useMemo(
        () => activeJobs.filter(j => j.runMode === 'parallel' && (j.status === 'done' || j.status === 'failed')),
        [activeJobs]
    );

    useEffect(() => {
        if (parallelDoneJobs.length === 0) return;

        const unsubscribes = parallelDoneJobs.map((job) => {
            const conflictDocRef = getWorkspaceSubdocRef(workspaceId, `agentConflicts/${job.id}`);
            return onSnapshot(conflictDocRef, (snap) => {
                if (!snap.exists()) return;
                const data = snap.data();
                if (data?.status === 'pending_resolution' && data.conflicts?.length > 0) {
                    setConflictsByJob(prev => ({ ...prev, [job.id]: data }));
                } else {
                    setConflictsByJob(prev => {
                        const next = { ...prev };
                        delete next[job.id];
                        return next;
                    });
                }
            });
        });

        return () => unsubscribes.forEach(u => u());
    }, [parallelDoneJobs, workspaceId]);

    const handleResolve = useCallback(async (jobId, agentId, filePath, choice, branchName) => {
        const key = `${jobId}:${agentId}:${filePath}`;
        setResolving(prev => ({ ...prev, [key]: true }));
        try {
            await apiManager.resolveAgentConflict(workspaceId, jobId, agentId, filePath, choice, branchName);
        } finally {
            setResolving(prev => {
                const next = { ...prev };
                delete next[key];
                return next;
            });
        }
    }, [workspaceId]);

    const conflictEntries = Object.entries(conflictsByJob);
    if (conflictEntries.length === 0) return null;

    return (
        <div className="mt-3 space-y-3">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-400">Merge Conflicts</p>
            {conflictEntries.map(([jobId, conflictDoc]) =>
                conflictDoc.conflicts.map((agentConflict) =>
                    agentConflict.conflicts.map((fileConflict) => {
                        const key = `${jobId}:${agentConflict.agentId}:${fileConflict.filePath}`;
                        const isPending = resolving[key];
                        const fileName = fileConflict.filePath.split('/').pop();
                        return (
                            <div key={key} className="rounded-md border border-amber-400/30 bg-amber-400/5 p-3 space-y-2">
                                <div>
                                    <p className="text-[11px] font-semibold text-on-surface truncate">{fileName}</p>
                                    <p className="text-[10px] text-on-surface-dim truncate">{agentConflict.task}</p>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        disabled={isPending}
                                        onClick={() => handleResolve(jobId, agentConflict.agentId, fileConflict.filePath, 'main', agentConflict.branchName)}
                                        className="flex-1 rounded border border-border-color bg-background-dark/60 py-1.5 text-[10px] font-semibold text-on-surface-dim transition-colors hover:border-primary/40 hover:text-on-surface disabled:opacity-50"
                                    >
                                        Keep current
                                    </button>
                                    <button
                                        type="button"
                                        disabled={isPending}
                                        onClick={() => handleResolve(jobId, agentConflict.agentId, fileConflict.filePath, 'branch', agentConflict.branchName)}
                                        className="flex-1 rounded border border-primary/40 bg-primary/10 py-1.5 text-[10px] font-semibold text-primary transition-colors hover:bg-primary/20 disabled:opacity-50"
                                    >
                                        {isPending ? 'Applying…' : "Take agent's"}
                                    </button>
                                </div>
                            </div>
                        );
                    })
                )
            )}
        </div>
    );
};

const WorkspaceInlineTodoList = ({ workspaceId }) => {
    const [authState, setAuthState] = useState(() => authManager.getState());
    const [todos, setTodos] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('todo');
    const [draftTodo, setDraftTodo] = useState('');
    const [isSubmittingTodo, setIsSubmittingTodo] = useState(false);
    const [draftJob, setDraftJob] = useState('');
    const [isSubmittingJob, setIsSubmittingJob] = useState(false);
    const [isLaunchingAuto, setIsLaunchingAuto] = useState(false);
    const [runMode, setRunMode] = useState('sequential');
    const [showRunModal, setShowRunModal] = useState(false);
    const [copiedTodoId, setCopiedTodoId] = useState(null);

    useEffect(() => {
        const unsubscribe = authManager.subscribe(setAuthState);
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (!authState.user?.uid || !workspaceId) {
            setTodos([]);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        const todosCollectionRef = getWorkspaceSubcollectionRef(db, authState.user.uid, workspaceId, 'todos');

        const unsubscribe = onSnapshot(todosCollectionRef,
            (snapshot) => {
                const nextTodos = snapshot.docs.map((todoDoc) => ({
                    id: todoDoc.id,
                    ...todoDoc.data(),
                }));
                setTodos(nextTodos);
                setIsLoading(false);
            },
            (error) => {
                console.error('Failed to load workspace todos:', error);
                setTodos([]);
                setIsLoading(false);
            }
        );

        return () => unsubscribe();
    }, [authState.user?.uid, workspaceId]);

    const humanTodos = useMemo(() => {
        return [...todos]
            .filter((t) => !t.type)
            .sort((left, right) => {
                const leftCompleted = left.completed ? 1 : 0;
                const rightCompleted = right.completed ? 1 : 0;
                if (leftCompleted !== rightCompleted) return leftCompleted - rightCompleted;
                return (normalizeDate(right.updatedAt || right.createdAt)?.getTime() || 0) - (normalizeDate(left.updatedAt || left.createdAt)?.getTime() || 0);
            });
    }, [todos]);

    const queuedJobs = useMemo(() => {
        return [...todos]
            .filter((t) => t.type === 'agent_queued')
            .sort((a, b) => (normalizeDate(a.createdAt)?.getTime() || 0) - (normalizeDate(b.createdAt)?.getTime() || 0));
    }, [todos]);

    const activeJobs = useMemo(() => {
        return [...todos]
            .filter((t) => t.type === 'agent_job')
            .sort((a, b) => (normalizeDate(b.updatedAt || b.createdAt)?.getTime() || 0) - (normalizeDate(a.updatedAt || a.createdAt)?.getTime() || 0));
    }, [todos]);

    const isAgentsRunning = activeJobs.some(j => j.status === 'running' || j.status === 'dispatched');
    const doneCount = activeJobs.filter(j => j.status === 'done').length;
    const totalActiveCount = activeJobs.length;

    const handleAddTodo = async (event) => {
        event.preventDefault();
        const nextTodo = draftTodo.trim();
        if (!nextTodo || isSubmittingTodo || !authState.user?.uid || !workspaceId) return;
        try {
            setIsSubmittingTodo(true);
            await addDoc(getWorkspaceSubcollectionRef(db, authState.user.uid, workspaceId, 'todos'), {
                content: nextTodo,
                completed: false,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });
            setDraftTodo('');
        } finally {
            setIsSubmittingTodo(false);
        }
    };

    const handleAddJob = async (event) => {
        event.preventDefault();
        const nextJob = draftJob.trim();
        if (!nextJob || isSubmittingJob || !authState.user?.uid || !workspaceId) return;
        try {
            setIsSubmittingJob(true);
            await addDoc(getWorkspaceSubcollectionRef(db, authState.user.uid, workspaceId, 'todos'), {
                content: nextJob,
                type: 'agent_queued',
                status: 'queued',
                completed: false,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });
            setDraftJob('');
        } finally {
            setIsSubmittingJob(false);
        }
    };

    const handleDeleteTodo = async (todoId) => {
        if (!authState.user?.uid || !workspaceId) return;
        await deleteDoc(getWorkspaceSubdocRef(db, authState.user.uid, workspaceId, 'todos', todoId));
    };

    const handleCopyTodo = (todo) => {
        navigator.clipboard.writeText(todo.content || '');
        setCopiedTodoId(todo.id);
        setTimeout(() => setCopiedTodoId(null), 1500);
    };

    const handleToggleTodo = async (todo) => {
        if (!authState.user?.uid || !workspaceId || !todo?.id) return;
        await updateDoc(getWorkspaceSubdocRef(db, authState.user.uid, workspaceId, 'todos', todo.id), {
            completed: !todo.completed,
            updatedAt: serverTimestamp(),
        });
    };

    const handleRunFullAuto = async (mode) => {
        if (queuedJobs.length === 0 || isLaunchingAuto) return;
        const resolvedMode = mode || runMode;
        setRunMode(resolvedMode);
        try {
            setIsLaunchingAuto(true);
            await apiManager.runFullAuto(workspaceId, null, resolvedMode);
        } catch (err) {
            console.error('Run Full Auto failed:', err);
        } finally {
            setIsLaunchingAuto(false);
        }
    };

    return (
        <div className="bg-surface rounded-lg border" style={{ borderColor: 'rgba(var(--border-color-rgb), 0.5)' }}>
            {/* Tab header */}
            <div className="flex border-b" style={{ borderColor: 'rgba(var(--border-color-rgb), 0.5)' }}>
                <button
                    type="button"
                    onClick={() => setActiveTab('todo')}
                    className={`flex items-center gap-1.5 px-3 py-2 text-[10px] font-semibold transition-colors ${
                        activeTab === 'todo'
                            ? 'border-b-2 border-primary text-primary'
                            : 'text-on-surface-dim hover:text-on-surface'
                    }`}
                >
                    <CheckSquare2 size={11} />
                    All Todos
                </button>
                <button
                    type="button"
                    onClick={() => setActiveTab('queue')}
                    className={`flex items-center gap-1.5 px-3 py-2 text-[10px] font-semibold transition-colors ${
                        activeTab === 'queue'
                            ? 'border-b-2 border-primary text-primary'
                            : 'text-on-surface-dim hover:text-on-surface'
                    }`}
                >
                    <Bot size={11} />
                    Agent Todos
                    {isAgentsRunning ? (
                        <span className="flex items-center gap-1 rounded-full bg-green-400/15 px-1.5 py-0.5 text-[9px] font-semibold text-green-400 animate-pulse">
                            <span className="h-1.5 w-1.5 rounded-full bg-green-400" />
                            {doneCount}/{totalActiveCount}
                        </span>
                    ) : queuedJobs.length > 0 ? (
                        <span className="rounded-full bg-primary/20 px-1.5 py-0.5 text-[9px] text-primary">
                            {queuedJobs.length}
                        </span>
                    ) : null}
                </button>
            </div>

            <div className="p-3">
                {activeTab === 'todo' && (
                    <>
                        <form onSubmit={handleAddTodo} className="mb-3 flex items-center gap-2">
                            <input
                                type="text"
                                value={draftTodo}
                                onChange={(event) => setDraftTodo(event.target.value)}
                                placeholder="Add a task"
                                className="h-8 flex-1 rounded-md border border-border-color bg-background-dark/60 px-3 text-[11px] text-on-surface placeholder:text-on-surface-dim focus:border-primary/40 focus:outline-none"
                            />
                            <button
                                type="submit"
                                disabled={!draftTodo.trim() || isSubmittingTodo}
                                className="h-8 rounded-md bg-primary px-3 text-[10px] font-semibold text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                Add
                            </button>
                        </form>

                        {isLoading && humanTodos.length === 0 ? (
                            <p className="text-[11px] text-on-surface-dim">Loading...</p>
                        ) : humanTodos.length === 0 ? (
                            <p className="text-[11px] text-on-surface-dim">No todos yet.</p>
                        ) : (
                            <div className="space-y-2">
                                {humanTodos.map((todo) => (
                                    <div
                                        key={todo.id}
                                        className="flex items-start gap-2 rounded-md border border-border-color bg-background-dark/60 px-3 py-2 text-left transition-colors hover:border-primary/30"
                                    >
                                        <button
                                            type="button"
                                            onClick={() => handleToggleTodo(todo)}
                                            className="mt-0.5 text-primary"
                                            aria-label={todo.completed ? 'Mark todo incomplete' : 'Mark todo complete'}
                                        >
                                            {todo.completed ? <CheckSquare2 size={14} /> : <Square size={14} />}
                                        </button>
                                        <span className={`flex-1 text-[11px] leading-relaxed ${todo.completed ? 'text-on-surface-dim line-through' : 'text-on-surface'}`}>
                                            {todo.content}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => handleCopyTodo(todo)}
                                            className="text-on-surface-dim transition-colors hover:text-primary"
                                            aria-label="Copy todo"
                                        >
                                            {copiedTodoId === todo.id ? <Check size={13} /> : <Copy size={13} />}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleDeleteTodo(todo.id)}
                                            className="text-on-surface-dim transition-colors hover:text-red-300"
                                            aria-label="Delete todo"
                                        >
                                            <Trash2 size={13} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}

                {activeTab === 'queue' && (
                    <>
                        <form onSubmit={handleAddJob} className={`mb-3 flex items-center gap-2 ${isAgentsRunning ? 'opacity-40 pointer-events-none' : ''}`}>
                            <input
                                type="text"
                                value={draftJob}
                                onChange={(event) => setDraftJob(event.target.value)}
                                placeholder={isAgentsRunning ? 'Agents are running…' : 'Describe a task for the agent'}
                                disabled={isAgentsRunning}
                                className="h-8 flex-1 rounded-md border border-border-color bg-background-dark/60 px-3 text-[11px] text-on-surface placeholder:text-on-surface-dim focus:border-primary/40 focus:outline-none disabled:cursor-not-allowed"
                            />
                            <button
                                type="submit"
                                disabled={!draftJob.trim() || isSubmittingJob || isAgentsRunning}
                                className="h-8 rounded-md bg-primary px-3 text-[10px] font-semibold text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                Add
                            </button>
                        </form>

                        {queuedJobs.length > 0 && (
                            <div className={`mb-3 space-y-1.5 ${isAgentsRunning ? 'opacity-40 pointer-events-none' : ''}`}>
                                {queuedJobs.map((job) => (
                                    <div
                                        key={job.id}
                                        className="flex items-center gap-2 rounded-md border border-border-color bg-background-dark/60 px-3 py-2"
                                    >
                                        <Clock size={12} className="shrink-0 text-on-surface-dim" />
                                        <span className="flex-1 text-[11px] text-on-surface">{job.content}</span>
                                        {!isAgentsRunning && (
                                            <button
                                                type="button"
                                                onClick={() => handleDeleteTodo(job.id)}
                                                className="text-on-surface-dim transition-colors hover:text-red-300"
                                                aria-label="Remove job"
                                            >
                                                <Trash2 size={13} />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {isAgentsRunning ? (
                            <div className="mb-3 flex w-full items-center gap-2.5 rounded-md border border-green-400/30 bg-green-400/[0.07] px-3 py-2.5">
                                <Loader2 size={13} className="shrink-0 animate-spin text-green-400" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-[11px] font-semibold text-green-400">Agents running — {doneCount} of {totalActiveCount} done</p>
                                    <p className="text-[10px] text-on-surface-dim mt-0.5 truncate">
                                        {activeJobs.find(j => j.status === 'running')?.content || 'Starting next task…'}
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setShowRunModal(true)}
                                disabled={queuedJobs.length === 0 || isLaunchingAuto}
                                className="mb-3 flex w-full items-center justify-center gap-2 rounded-md bg-primary px-3 py-2 text-[11px] font-semibold text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-40"
                            >
                                {isLaunchingAuto ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
                                {isLaunchingAuto ? 'Launching…' : 'Schedule Full Auto Mode…'}
                            </button>
                        )}

                        <ConfirmRunAgentsModal
                            isOpen={showRunModal}
                            onClose={() => setShowRunModal(false)}
                            onConfirm={handleRunFullAuto}
                            jobs={queuedJobs}
                        />

                        {activeJobs.length > 0 && (
                            <>
                                <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-on-surface-dim">{isAgentsRunning ? 'Progress' : 'Done'}</p>
                                <div className="space-y-1.5">
                                    {activeJobs.map((job) => (
                                        <div
                                            key={job.id}
                                            className={`flex items-center gap-2 rounded-md border px-3 py-2 ${
                                                job.status === 'running'
                                                    ? 'border-green-400/30 bg-green-400/[0.05]'
                                                    : job.status === 'done'
                                                        ? 'border-border-color bg-background-dark/60 opacity-60'
                                                        : 'border-border-color bg-background-dark/60'
                                            }`}
                                        >
                                            <AgentJobStatusIcon status={job.status} />
                                            <div className="flex min-w-0 flex-1 flex-col">
                                                <span className={`truncate text-[11px] ${job.status === 'done' ? 'text-on-surface-dim line-through' : 'text-on-surface'}`}>{job.content}</span>
                                                <span className="text-[10px] text-on-surface-dim capitalize">{job.status}{job.runMode === 'parallel' ? ' · parallel' : ''}</span>
                                            </div>
                                            {(job.status === 'done' || job.status === 'failed') && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleDeleteTodo(job.id)}
                                                    className="text-on-surface-dim transition-colors hover:text-red-300"
                                                    aria-label="Dismiss"
                                                >
                                                    <Trash2 size={13} />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}

                        {/* Conflict resolution cards — shown when parallel agents had merge conflicts */}
                        <AgentConflictCards workspaceId={workspaceId} activeJobs={activeJobs} />
                    </>
                )}
            </div>
        </div>
    );
};


const OverviewTab = ({ workspace, onOpenFile, onDownloadFile, onInitiateRename, onInitiateDeleteFile, onCopyForVSCode, onForceRefresh, isManualRefreshing, onUploadZip, onOpenTerminal, onInitiateCreateFile, onInitiateCreateFolder, fileExplorerProps, onInitiateRefactor, explorerDropProps, showNotification, onDataRefresh }) => {
    const { conversations, currentConversation } = useConversation();
    const [isFileExplorerOpen, setIsFileExplorerOpen] = useState(true);
    const didDragRef = useRef(false);
    const lastSyncedWorkspaceIdRef = useRef(workspace?.id || null);
    const [isResizing, setIsResizing] = useState(false);
    const [isSummaryRefreshing, setIsSummaryRefreshing] = useState(false);
    const [explorerHeight, setExplorerHeight] = useState(() => {
        const savedHeight = localStorage.getItem('shelbula.fileExplorer.height');
        return savedHeight ? Math.max(100, parseInt(savedHeight, 10)) : 200;
    });
    const [summaryState, setSummaryState] = useState(() => (typeof workspace?.summary === 'string' ? workspace.summary.trim() : ''));
    const [summaryExpanded, setSummaryExpanded] = useState(false);
    const [summaryTreePreview, setSummaryTreePreview] = useState(() => (typeof workspace?.summaryTreePreview === 'string' ? workspace.summaryTreePreview.trim() : ''));
    const [summaryFilesObserved, setSummaryFilesObserved] = useState(() => Array.isArray(workspace?.summaryReadableFilesObserved) ? workspace.summaryReadableFilesObserved : []);

    const areObservedFilesEqual = useCallback((left, right) => {
        if (left === right) return true;
        if (!Array.isArray(left) || !Array.isArray(right)) return false;
        if (left.length !== right.length) return false;

        return left.every((value, index) => value === right[index]);
    }, []);

    useEffect(() => {
        localStorage.setItem('shelbula.fileExplorer.height', explorerHeight);
    }, [explorerHeight]);

    useEffect(() => {
        const incomingWorkspaceId = workspace?.id || null;
        const incomingSummary = typeof workspace?.summary === 'string' ? workspace.summary.trim() : '';
        const incomingTreePreview = typeof workspace?.summaryTreePreview === 'string' ? workspace.summaryTreePreview.trim() : '';
        const incomingFilesObserved = Array.isArray(workspace?.summaryReadableFilesObserved) ? workspace.summaryReadableFilesObserved : [];
        const hasIncomingSummaryPayload = Boolean(incomingSummary || incomingTreePreview || incomingFilesObserved.length);
        const workspaceChanged = lastSyncedWorkspaceIdRef.current !== incomingWorkspaceId;

        lastSyncedWorkspaceIdRef.current = incomingWorkspaceId;

        if (workspaceChanged || hasIncomingSummaryPayload) {
            setSummaryState((current) => current === incomingSummary ? current : incomingSummary);
            setSummaryTreePreview((current) => current === incomingTreePreview ? current : incomingTreePreview);
            setSummaryFilesObserved((current) => areObservedFilesEqual(current, incomingFilesObserved) ? current : incomingFilesObserved);
        }
    }, [workspace?.id, workspace?.summary, workspace?.summaryTreePreview, workspace?.summaryReadableFilesObserved, areObservedFilesEqual]);
    const handleBarClick = () => {
        if (didDragRef.current) { didDragRef.current = false; return; }
        setIsFileExplorerOpen(prev => !prev);
    };

    const handleBarMouseDown = (e) => {
        if (!isFileExplorerOpen) return;
        e.preventDefault();
        didDragRef.current = false;
        const startY = e.clientY;
        const startHeight = explorerHeight;
        const handleMouseMove = (moveEvent) => {
            didDragRef.current = true;
            setIsResizing(true);
            const newHeight = startHeight - (moveEvent.clientY - startY);
            setExplorerHeight(Math.max(100, Math.min(newHeight, window.innerHeight * 0.9)));
        };
        const handleMouseUp = () => {
            setIsResizing(false);
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
    };

    const handleRefresh = (e) => {
        e.stopPropagation();
        onForceRefresh?.();
    };

    const handleSummaryRefresh = async (event) => {
        event?.preventDefault?.();
        event?.stopPropagation?.();

        if (isSummaryRefreshing) return;
        if (!workspace?.id) {
            showNotification?.('This workspace is missing an id, so a summary cannot be generated yet.', 'error');
            return;
        }
        const actionLabel = workspaceSummary ? 'Refreshing' : 'Generating';

        try {
            setIsSummaryRefreshing(true);
            showNotification?.(`${actionLabel} workspace summary...`, 'info');
            const fallbackModel = botManager.getState().currentModel;
            const result = await apiManager.regenerateWorkspaceSummary(workspace.id, {
                platform: String(currentConversation?.botPlatform || fallbackModel?.platform || '').trim().toUpperCase(),
                model: String(currentConversation?.botModel || fallbackModel?.model || '').trim() || null,
            });
            const nextSummary = typeof result?.summary === 'string' ? result.summary.trim() : '';
            const nextTreePreview = typeof result?.treePreview === 'string' ? result.treePreview.trim() : '';
            const nextFilesObserved = Array.isArray(result?.readableFilesObserved) ? result.readableFilesObserved : [];

            if (!nextSummary) {
                throw new Error('Workspace summary response was empty.');
            }

            setSummaryState(nextSummary);
            setSummaryTreePreview(nextTreePreview);
            setSummaryFilesObserved(nextFilesObserved);
            onDataRefresh?.();
            showNotification?.(`Workspace summary ${workspaceSummary ? 'refreshed' : 'generated'}.`, 'success');
        } catch (error) {
            showNotification?.(`Failed to refresh workspace summary: ${error.message}`, 'error');
        } finally {
            setIsSummaryRefreshing(false);
        }
    };

    const rootItemCount = useMemo(() => Object.keys(fileExplorerProps?.files || {}).length, [fileExplorerProps?.files]);
    const recentConversationCount = useMemo(() => {
        if (!workspace?.id) {
            return 0;
        }

        return Object.values(conversations || {})
            .filter((conversation) => areWorkspaceContextIdsEqual(conversation?.workspaceContextId, workspace.id))
            .filter((conversation) => hasVisibleWorkspaceMessages(conversation))
            .length;
    }, [conversations, workspace?.id]);

    const workspaceSummary = typeof summaryState === 'string' && summaryState.trim()
        ? summaryState.trim()
        : null;
    const hasSummaryTreePreview = typeof summaryTreePreview === 'string' && summaryTreePreview.trim();
    const hasSummaryFilesObserved = Array.isArray(summaryFilesObserved) && summaryFilesObserved.length > 0;

    return (
        <div className="flex flex-col h-full">
            <div className="flex-1 p-4 space-y-4 overflow-y-auto custom-scrollbar">
                <div className="bg-surface rounded-lg border p-3" style={{ borderColor: 'rgba(var(--primary-rgb), 0.22)' }}>
                    <div className="flex items-center justify-between gap-3 mb-2">
                        <OverviewCardTitle icon={LayoutDashboard} label="Workspace Summary" />
                        <button
                            type="button"
                            onClick={handleSummaryRefresh}
                            disabled={isSummaryRefreshing || !workspace?.id}
                            className="inline-flex items-center gap-1.5 rounded-md border border-border-color px-2 py-1 text-[11px] font-medium text-on-surface transition-colors hover:bg-surface-dark disabled:cursor-wait disabled:opacity-70"
                            aria-busy={isSummaryRefreshing}
                            title={
                                !workspace?.id
                                    ? 'Workspace summary is unavailable until this workspace has a valid id.'
                                    : undefined
                            }
                        >
                            {isSummaryRefreshing ? <Loader size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                            <span>{workspaceSummary ? 'Refresh' : 'Generate'}</span>
                        </button>
                    </div>
                    <div className={!summaryExpanded ? 'max-h-[400px] overflow-hidden' : ''}>
                        {workspaceSummary ? <p className="text-[12px] leading-relaxed text-on-surface">{workspaceSummary}</p> : null}
                        {hasSummaryTreePreview ? (
                            <pre className="mt-3 overflow-x-auto rounded-md bg-background-darker p-3 text-[11px] leading-relaxed text-on-surface-dim custom-scrollbar whitespace-pre-wrap">{summaryTreePreview}</pre>
                        ) : null}
                        {hasSummaryFilesObserved ? (
                            <div className="mt-3 flex flex-wrap gap-1.5">
                                {summaryFilesObserved.map((filePath) => (
                                    <span key={filePath} className="rounded-full bg-background-darker px-2 py-1 text-[10px] text-on-surface-dim">
                                        {filePath}
                                    </span>
                                ))}
                            </div>
                        ) : null}
                    </div>
                    {workspaceSummary && (
                        <button
                            type="button"
                            onClick={() => setSummaryExpanded((v) => !v)}
                            className="mt-2 text-[10px] text-on-surface-dim hover:text-primary transition-colors"
                        >
                            {summaryExpanded ? 'Show less' : 'More…'}
                        </button>
                    )}
                </div>

                <WorkspaceContextCard rootItemCount={rootItemCount} recentConversationCount={recentConversationCount} hasRepo={Boolean(workspace.repoUrl)} />

                <WorkspaceInlineTodoList workspaceId={workspace.id} />

                <WorkspaceConversationList workspaceId={workspace.id} />
            </div>
            <div className="flex-shrink-0 flex flex-col">
                <div className="p-2 border-t border-b border-border-color flex items-center justify-between hover:bg-surface-dark transition-colors" style={{ cursor: isFileExplorerOpen ? 'ns-resize' : 'pointer' }} onClick={handleBarClick} onMouseDown={handleBarMouseDown}>
                    <h3 className="text-[13px] font-semibold text-on-surface flex items-center gap-2"><FolderTree size={16} className="text-primary"/>File Explorer</h3>
                    <div className="flex items-center gap-2">
                        <button onClick={handleRefresh} disabled={isManualRefreshing} className="p-1 rounded-md hover:bg-surface text-on-surface-dim hover:text-on-surface disabled:cursor-wait" title="Sync & Refresh Files">
                            <RefreshCw size={14} className={isManualRefreshing ? 'animate-spin' : ''} />
                        </button>
                        {isFileExplorerOpen ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
                    </div>
                </div>
                <AnimatePresence>
                    {isFileExplorerOpen && (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: explorerHeight, opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: isResizing ? 0 : 0.3, ease: 'easeInOut' }} className="overflow-hidden bg-background-dark">
                            <WorkspaceDropTarget {...explorerDropProps}>
                                <FileExplorer {...fileExplorerProps} onOpenFile={onOpenFile} onDownloadFile={onDownloadFile} onInitiateRename={onInitiateRename} onInitiateDeleteFile={onInitiateDeleteFile} onCopyForVSCode={onCopyForVSCode} onUploadZip={onUploadZip} onOpenTerminal={onOpenTerminal} onInitiateCreateFile={onInitiateCreateFile} onInitiateCreateFolder={onInitiateCreateFolder} onInitiateRefactor={onInitiateRefactor} />
                            </WorkspaceDropTarget>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

const TABS = {
    overview: { label: 'Workspace', icon: LayoutDashboard, component: OverviewTab },
    workspaceHistory: { label: 'Changes', icon: History, component: WorkspaceHistoryTab },
};

const WorkspaceDetailPanel = ({ isWorkspaceExplorerOnlyMode, workspace, onInitiateUndo, onInitiateCancel, onInitiateRestart, onInitiateRevertToPoint, onInitiateRollbackToPlan, onOpenFile, onDownloadFile, onInitiateRename, onInitiateDeleteFile, onCopyForVSCode, onDropFiles, onUploadZip, onOpenTerminal, showNotification, onDataRefresh, dataRefreshTrigger, onFullFileListLoad, activeTab, setActiveTab, onInitiateCreateFile, onInitiateCreateFolder, onInitiateRefactor, activeFilePath, pinnedFilePaths, onToggleFilePin, isBrainEnabled }) => {
    const [isExplorerDragging, setIsExplorerDragging] = useState(false);
    const { currentConversation } = useConversation();
    const isIncognitoActive = currentConversation?.privacyMode === 'incognito';
    const { setActiveDropZone, resetDragCounter } = useDragDrop();
    const panelRef = useRef(null);
    const explorerDragDepthRef = useRef(0);

    const [fileTree, setFileTree] = useState({});
    const [expandedFolders, setExpandedFolders] = useState(new Set());
    const [isTreeLoading, setIsTreeLoading] = useState(true);
    const [treeError, setTreeError] = useState(null);
    const [isManualRefreshing, setIsManualRefreshing] = useState(false);

    const refreshFileTree = useCallback(async () => {
        const data = await apiManager.listAllFiles(workspace.id);
        const files = Array.isArray(data?.files) ? data.files : [];
        setFileTree(buildFileTreeFromFlatFiles(files));
        setTreeError(null);
        onFullFileListLoad((files || []).filter(file => !file.path.startsWith('.ProjectBrain')));
        return files;
    }, [workspace.id, onFullFileListLoad]);

    const loadDirectory = useCallback(async (path = '') => {
        try {
            const data = await apiManager.listDirectory(workspace.id, path);
            const children = (data.entries || []).reduce((acc, entry) => {
                acc[entry.name] = entry;
                return acc;
            }, {});

            if (path === '') {
                setFileTree(children);
            } else {
                setFileTree(currentTree => updateNodeInTree(currentTree, path, { children, isLoading: false }));
            }
            setTreeError(null);
        } catch (err) {
            setTreeError(err.message);
            if (err?.status !== 404) {
                showNotification(`Error loading files: ${err.message}`, 'error');
            }
        }
    }, [workspace.id, showNotification]);

    const handleToggleFolder = useCallback(async (node) => {
        const { path } = node;
        const newExpandedFolders = new Set(expandedFolders);
        if (expandedFolders.has(path)) {
            newExpandedFolders.delete(path);
        } else {
            newExpandedFolders.add(path);
            if (!node.children || Object.keys(node.children).length === 0) {
                setFileTree(currentTree => updateNodeInTree(currentTree, path, { isLoading: true }));
                await loadDirectory(path);
            }
        }
        setExpandedFolders(newExpandedFolders);
    }, [expandedFolders, loadDirectory]);

    const handleForceRefresh = useCallback(async () => {
        setIsManualRefreshing(true);
        setIsTreeLoading(true);
        try {
            await refreshFileTree();
            showNotification?.('Workspace files refreshed.', 'success');
        } catch (error) {
            setTreeError(error.message);
            showNotification?.(`Error refreshing files: ${error.message}`, 'error');
        } finally {
            setIsTreeLoading(false);
            setIsManualRefreshing(false);
        }
    }, [refreshFileTree, showNotification]);

    const clearExplorerDropState = useCallback(() => {
        explorerDragDepthRef.current = 0;
        setIsExplorerDragging(false);
        setActiveDropZone(null);
    }, [setActiveDropZone]);

    const handleExplorerDragEnter = useCallback((event) => {
        if (!hasFilePayload(event)) return;
        event.preventDefault();
        event.stopPropagation();
        explorerDragDepthRef.current += 1;
        setIsExplorerDragging(true);
        setActiveDropZone(`workspace:${workspace.id}:files`);
    }, [setActiveDropZone, workspace.id]);

    const handleExplorerDragOver = useCallback((event) => {
        if (!hasFilePayload(event)) return;
        event.preventDefault();
        event.stopPropagation();
        if (!isExplorerDragging) setIsExplorerDragging(true);
        setActiveDropZone(`workspace:${workspace.id}:files`);
    }, [isExplorerDragging, setActiveDropZone, workspace.id]);

    const handleExplorerDragLeave = useCallback((event) => {
        if (!hasFilePayload(event)) return;
        event.preventDefault();
        event.stopPropagation();
        explorerDragDepthRef.current = Math.max(0, explorerDragDepthRef.current - 1);
        if (explorerDragDepthRef.current === 0) {
            setIsExplorerDragging(false);
            setActiveDropZone(null);
        }
    }, [setActiveDropZone]);

    const handleExplorerDrop = useCallback(async (event) => {
        if (!hasFilePayload(event)) return;
        event.preventDefault();
        event.stopPropagation();

        const droppedFiles = event.dataTransfer?.files;
        clearExplorerDropState();
        resetDragCounter();

        if (!droppedFiles?.length) return;

        try {
            await onDropFiles(workspace.id, droppedFiles);
        } catch (error) {
            console.error('Workspace file drop failed:', error);
        }
    }, [clearExplorerDropState, onDropFiles, resetDragCounter, workspace.id]);

    useEffect(() => {
        if (!isWorkspaceExplorerOnlyMode && workspace?.id) {
            setActiveTab('overview');
        }
        clearExplorerDropState();
        if (workspace?.id && !['CLONING', 'CREATING'].includes(workspace.status)) {
            setIsTreeLoading(true);
            refreshFileTree()
                .catch((err) => {
                    setTreeError(err.message);
                    console.error('Failed to load file tree:', err);
                })
                .finally(() => setIsTreeLoading(false));
        }
        return () => {
            clearExplorerDropState();
        };
    }, [workspace?.id, workspace?.status, isWorkspaceExplorerOnlyMode, setActiveTab, dataRefreshTrigger, refreshFileTree, clearExplorerDropState]);

    if (!workspace) {
        return <div className="h-full flex items-center justify-center bg-background-darker"><p className="text-[12px] text-on-surface-dim">Select a workspace to view details.</p></div>;
    }

    if (workspace.status === 'CLONING' || workspace.status === 'CREATING') {
        return (
            <div className="h-full flex flex-col items-center justify-center bg-background-darker text-on-surface-dim p-4 text-center">
                <Loader className="animate-spin text-primary h-8 w-8 mb-4" />
                <p className="text-base font-semibold text-on-surface">
                    {workspace.status === 'CLONING' ? 'Cloning Repository...' : 'Preparing Workspace...'}
                </p>
                <p className="text-[13px] mt-1">
                    The workspace <span className="font-semibold text-on-surface">{workspace.workspaceName || workspace.id}</span> will be ready shortly.
                </p>
                <p className="text-[11px] mt-2 text-on-surface-dim">This view will automatically update upon completion.</p>
            </div>
        );
    }

    const fileExplorerProps = {
        files: fileTree,
        isLoading: isTreeLoading,
        error: treeError,
        onToggleFolder: handleToggleFolder,
        expandedFolders: expandedFolders,
        activeFilePath,
        pinnedFilePaths,
        onToggleFilePin,
        isBrainEnabled,
    };

    if (isWorkspaceExplorerOnlyMode) {
        return (
            <div ref={panelRef} className="h-full flex flex-col bg-background-darker relative">
                <WorkspaceDropTarget
                    isDragging={isExplorerDragging}
                    onDragEnter={handleExplorerDragEnter}
                    onDragLeave={handleExplorerDragLeave}
                    onDragOver={handleExplorerDragOver}
                    onDrop={handleExplorerDrop}
                >
                    <FileExplorer {...fileExplorerProps} onOpenFile={onOpenFile} onDownloadFile={onDownloadFile} onInitiateRename={onInitiateRename} onInitiateDeleteFile={onInitiateDeleteFile} onCopyForVSCode={onCopyForVSCode} onUploadZip={onUploadZip} onOpenTerminal={onOpenTerminal} onInitiateCreateFile={onInitiateCreateFile} onInitiateCreateFolder={onInitiateCreateFolder} onInitiateRefactor={onInitiateRefactor} />
                </WorkspaceDropTarget>
            </div>
        );
    }

    const availableTabs = TABS;
    const ActiveComponent = availableTabs[activeTab]?.component;
    if (!ActiveComponent) {
        setActiveTab('overview');
        return null;
    }

    const commonProps = { workspace };
    const tabSpecificProps = {
        overview: {
            onOpenFile, onDownloadFile, onInitiateRename, onInitiateDeleteFile,
            onCopyForVSCode, onUploadZip, onOpenTerminal, onInitiateCreateFile,
            onInitiateCreateFolder, onForceRefresh: handleForceRefresh, isManualRefreshing,
            fileExplorerProps, onInitiateRefactor, showNotification, onDataRefresh,
            explorerDropProps: {
                isDragging: isExplorerDragging,
                onDragEnter: handleExplorerDragEnter,
                onDragLeave: handleExplorerDragLeave,
                onDragOver: handleExplorerDragOver,
                onDrop: handleExplorerDrop,
            },
        },
        workspaceHistory: {
            onInitiateUndo, onInitiateCancel, onInitiateRestart, onInitiateRevertToPoint,
            onInitiateRollbackToPlan, refreshTrigger: dataRefreshTrigger,
        }
    };

    return (
        <div
            ref={panelRef}
            className={`h-full flex flex-col bg-background-darker relative transition-opacity duration-300 ${isIncognitoActive ? 'opacity-30 pointer-events-none select-none' : ''}`}
        >
            <div className="flex-shrink-0 border-b border-border-color px-4 h-[46px] flex items-center justify-between">
                <nav className="flex items-center h-full">
                    {Object.entries(availableTabs).map(([key, { label, icon: Icon }]) => (
                        <button key={key} onClick={() => setActiveTab(key)} className={`flex items-center gap-1.5 px-2.5 h-full text-[11px] font-medium border-b transition-colors ${activeTab === key ? 'border-primary text-primary' : 'border-transparent text-on-surface-dim hover:text-on-surface'}`}>
                            <Icon size={13} /><span>{label}</span>
                        </button>
                    ))}
                </nav>
                <div />
            </div>
            
            <div className="flex-1 overflow-y-auto">
                <ActiveComponent {...commonProps} {...tabSpecificProps[activeTab]} />
            </div>
        </div>
    );
};

export default WorkspaceDetailPanel;